"use strict";
import { TradeType } from "@uniswap/sdk-core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { areCurrencyIdsEqual, currencyId } from "utils/uniswapCurrencyId";
import { useBscQuote } from "features/routing/hooks/useBscQuote";
import { use0xQuote } from "features/routing/hooks/use0xQuote";
import { useOpenOceanQuote } from "features/routing/hooks/useOpenOceanQuote";
import { useTransactionSettingsContext } from "features/transactions/settings/contexts/TransactionSettingsContext";
import { resolveRouteSources } from "features/transactions/swap/utils/routingProvider";
export function useTrade({
  amountSpecified: amount,
  otherCurrency,
  tradeType,
  skip
}) {
  const { routeSources: sources } = useTransactionSettingsContext();
  const routeSources = resolveRouteSources(sources);
  const currencyIn = tradeType === TradeType.EXACT_INPUT ? amount?.currency : otherCurrency;
  const currencyOut = tradeType === TradeType.EXACT_OUTPUT ? amount?.currency : otherCurrency;
  const currencyInEqualsCurrencyOut = currencyIn && currencyOut && areCurrencyIdsEqual(currencyId(currencyIn), currencyId(currencyOut));
  const isZeroAmount = amount?.quotient.toString() === "0";
  const baseSkip = skip || !amount || isZeroAmount || currencyInEqualsCurrencyOut || !currencyIn || !currencyOut;
  const bscTokenIn = currencyIn?.isNative ? currencyIn.wrapped.address : currencyIn?.address;
  const bscTokenOut = currencyOut?.isNative ? currencyOut.wrapped.address : currencyOut?.address;
  const bscAmount = amount?.quotient.toString();
  const bothV2V3Disabled = !routeSources.v2 && !routeSources.v3;
  const allSourcesDisabled = bothV2V3Disabled && !routeSources.zeroX && !routeSources.openOcean;
  const topazResult = useBscQuote({
    tokenIn: bscTokenIn,
    tokenOut: bscTokenOut,
    amountSpecified: bscAmount,
    tradeType,
    chainId: currencyIn?.chainId,
    skip: baseSkip || bothV2V3Disabled,
    skipV2: !routeSources.v2,
    skipV3: !routeSources.v3
  });
  const zeroXResult = use0xQuote({
    tokenIn: bscTokenIn,
    tokenOut: bscTokenOut,
    amountSpecified: bscAmount,
    tradeType,
    chainId: currencyIn?.chainId,
    skip: baseSkip || !routeSources.zeroX || tradeType !== TradeType.EXACT_INPUT,
    currencyIn,
    currencyOut
  });
  const openOceanResult = useOpenOceanQuote({
    tokenIn: bscTokenIn,
    tokenOut: bscTokenOut,
    amountSpecified: bscAmount,
    tradeType,
    chainId: currencyIn?.chainId,
    skip: baseSkip || !routeSources.openOcean || tradeType !== TradeType.EXACT_INPUT,
    currencyIn,
    currencyOut
  });
  const topazRefetchRef = useRef(topazResult.refetch);
  topazRefetchRef.current = topazResult.refetch;
  const zeroXRefetchRef = useRef(zeroXResult.refetch);
  zeroXRefetchRef.current = zeroXResult.refetch;
  const openOceanRefetchRef = useRef(openOceanResult.refetch);
  openOceanRefetchRef.current = openOceanResult.refetch;
  const refetchAll = useCallback(() => {
    topazRefetchRef.current();
    zeroXRefetchRef.current();
    openOceanRefetchRef.current();
  }, []);
  const prevSourcesRef = useRef(routeSources);
  useEffect(() => {
    const prev = prevSourcesRef.current;
    if (prev.v2 !== routeSources.v2 || prev.v3 !== routeSources.v3 || prev.zeroX !== routeSources.zeroX || prev.openOcean !== routeSources.openOcean) {
      prevSourcesRef.current = routeSources;
      refetchAll();
    }
  }, [routeSources, refetchAll]);
  const lastTradeRef = useRef(null);
  const lastQuoteUpdatedAtRef = useRef(null);
  const GRACE_MS = 2e3;
  const fetchStartRef = useRef(0);
  const [, forceRender] = useState(0);
  const graceTimerRef = useRef(null);
  const isExactInput = tradeType === TradeType.EXACT_INPUT;
  const topazLoading = !bothV2V3Disabled && topazResult.isLoading;
  const zeroXLoading = routeSources.zeroX && isExactInput && zeroXResult.isLoading;
  const openOceanLoading = routeSources.openOcean && isExactInput && openOceanResult.isLoading;
  const allDone = !topazLoading && !zeroXLoading && !openOceanLoading;
  const anyLoading = topazLoading || zeroXLoading || openOceanLoading;
  useEffect(() => {
    if (anyLoading && fetchStartRef.current === 0) {
      fetchStartRef.current = Date.now();
      graceTimerRef.current = setTimeout(() => {
        forceRender((n) => n + 1);
      }, GRACE_MS);
    }
    if (allDone) {
      fetchStartRef.current = 0;
      if (graceTimerRef.current) {
        clearTimeout(graceTimerRef.current);
        graceTimerRef.current = null;
      }
    }
  }, [anyLoading, allDone]);
  useEffect(() => {
    return () => {
      if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
    };
  }, []);
  return useMemo(() => {
    if (allSourcesDisabled && !baseSkip) {
      return {
        trade: null,
        isLoading: false,
        error: new Error("All routing sources are disabled. Enable at least one source in swap settings."),
        refetch: refetchAll,
        quoteUpdatedAt: null
      };
    }
    const candidates = [];
    if (topazResult.trade) {
      candidates.push({ trade: topazResult.trade, quoteUpdatedAt: topazResult.quoteUpdatedAt });
    }
    if (zeroXResult.trade) {
      candidates.push({ trade: zeroXResult.trade, quoteUpdatedAt: zeroXResult.quoteUpdatedAt });
    }
    if (openOceanResult.trade) {
      candidates.push({
        trade: openOceanResult.trade,
        quoteUpdatedAt: openOceanResult.quoteUpdatedAt
      });
    }
    const hasPartialResult = candidates.length > 0;
    const withinGrace = fetchStartRef.current > 0 && Date.now() - fetchStartRef.current < GRACE_MS;
    const shouldWait = anyLoading && hasPartialResult && withinGrace && !lastTradeRef.current;
    let bestTrade = null;
    let bestQuoteUpdatedAt = null;
    let bestOut = -1n;
    for (const candidate of candidates) {
      const out = BigInt(candidate.trade.outputAmount?.quotient?.toString() ?? "0");
      if (out > bestOut) {
        bestOut = out;
        bestTrade = candidate.trade;
        bestQuoteUpdatedAt = candidate.quoteUpdatedAt;
      }
    }
    if (bestTrade && allDone) {
      lastTradeRef.current = bestTrade;
      lastQuoteUpdatedAtRef.current = bestQuoteUpdatedAt;
    }
    if (anyLoading && lastTradeRef.current) {
      return {
        trade: lastTradeRef.current,
        isLoading: true,
        error: null,
        refetch: refetchAll,
        quoteUpdatedAt: lastQuoteUpdatedAtRef.current
      };
    }
    if (shouldWait || anyLoading && !hasPartialResult) {
      return {
        trade: null,
        isLoading: true,
        error: null,
        refetch: refetchAll,
        quoteUpdatedAt: null
      };
    }
    if (bestTrade) {
      lastTradeRef.current = bestTrade;
      lastQuoteUpdatedAtRef.current = bestQuoteUpdatedAt;
    }
    return {
      trade: bestTrade,
      isLoading: false,
      error: !bestTrade ? topazResult.error || zeroXResult.error || openOceanResult.error : null,
      refetch: refetchAll,
      quoteUpdatedAt: bestQuoteUpdatedAt
    };
  }, [
    topazResult,
    zeroXResult,
    openOceanResult,
    refetchAll,
    anyLoading,
    allDone,
    allSourcesDisabled,
    baseSkip
  ]);
}
